import { useSyncExternalStore } from 'react';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { complianceKeys, removeComplianceQueries } from '@field-tickets/shared';
import { onAuthEpochChange } from './authEpoch';

/** The service worker's NetworkFirst API cache (vite.config.ts runtimeCaching). */
const API_CACHE_NAME = 'api-cache';

/**
 * The compliance register's API paths: the ones the service worker's rule
 * refuses to cache (vite.config.ts runtimeCaching; a test keeps them equal).
 */
export function isRegisterApiPath(pathname: string): boolean {
  return /^\/api\/(compliance|vehicles|me\/documents)(\/|$)/.test(pathname);
}

/**
 * Deletes the compliance register's responses and file bytes from the
 * service worker's API cache, left by a build that cached them. Best effort;
 * the SW no longer serves them.
 */
export async function purgeUncacheableApiEntries(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    if (!(await caches.has(API_CACHE_NAME))) return;
    const cache = await caches.open(API_CACHE_NAME);
    const requests = await cache.keys();
    await Promise.all(
      requests
        .filter((request) => isRegisterApiPath(new URL(request.url).pathname))
        .map((request) => cache.delete(request)),
    );
  } catch {
    /* Cache Storage unavailable (private mode, quota): nothing to purge. */
  }
}

// Set by a 401 from a register request of the current session. Holds until
// the signed-in identity changes (the epoch moves) or the user presses Retry:
// ComplianceRoute shows "session ended" instead of remounting, so a server
// that answers some register reads and refuses others cannot loop the page.
let sessionEnded = false;
const sessionEndedListeners = new Set<() => void>();

function setSessionEnded(next: boolean): void {
  if (sessionEnded === next) return;
  sessionEnded = next;
  for (const listener of sessionEndedListeners) listener();
}

function subscribeSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

/** True after a register 401, until the identity changes or the user retries. */
export function useComplianceSessionEnded(): boolean {
  return useSyncExternalStore(subscribeSessionEnded, () => sessionEnded);
}

/** The user's Retry on the "session ended" state: mount the register afresh. */
export function retryComplianceSession(): void {
  setSessionEnded(false);
}

function isComplianceKey(key: QueryKey | undefined): boolean {
  return Array.isArray(key) && key[0] === complianceKeys.all[0];
}

/**
 * Only an HTTP 401 of the current session. A request that started in a
 * departed session fails with StaleSessionError (no status) instead
 * (useCompliance's inSession), so the last user's late 401 is ignored.
 */
function isUnauthorized(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 401;
}

/**
 * The register's answer to a 401 (the session is gone): drop every register
 * query and its service-worker entries and end the register session, so a
 * page left open shows "session ended" instead of the last person's
 * documents. Once per session end: further 401s change nothing.
 */
function endComplianceSession(queryClient: QueryClient): void {
  if (sessionEnded) return;
  removeComplianceQueries(queryClient);
  void purgeUncacheableApiEntries();
  setSessionEnded(true);
}

/**
 * The compliance register's identity boundary (#1011): techs share phones,
 * so on every sign-out, expired session and user switch (the session epoch
 * moving), and on any 401 from a register query or mutation, the register's
 * queries leave the cache and its entries leave the service worker's API
 * cache. Also purges once at install, for entries a previous session left
 * behind before the app was closed.
 */
export function installComplianceSessionBoundary(queryClient: QueryClient): () => void {
  setSessionEnded(false);
  void purgeUncacheableApiEntries();
  const stopEpoch = onAuthEpochChange(() => {
    removeComplianceQueries(queryClient);
    void purgeUncacheableApiEntries();
    setSessionEnded(false);
  });
  const stopQueries = queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error'
      && isComplianceKey(event.query.queryKey) && isUnauthorized(event.action.error)) {
      endComplianceSession(queryClient);
    }
  });
  const stopMutations = queryClient.getMutationCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error'
      && isComplianceKey(event.mutation.options.mutationKey) && isUnauthorized(event.action.error)) {
      endComplianceSession(queryClient);
    }
  });
  return () => {
    stopEpoch();
    stopQueries();
    stopMutations();
  };
}
